/* global Word */

/**
 * Word Document Tools
 * ===================
 * Office.js implementations of document manipulation tools.
 * Uses paragraph-level iteration for text matching to avoid
 * control-character issues with body.search().
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Normalise a string for fuzzy paragraph-level comparison. */
function normalizeText(text: string): string {
  return text
    .replace(/[\r\n\v\f\u0007\u000B\u000C\u0085\u2028\u2029]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

// ---------------------------------------------------------------------------
// Read-only tools
// ---------------------------------------------------------------------------

export async function getDocumentText(): Promise<string> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("text");
    await context.sync();

    const lines: string[] = [];
    for (let i = 0; i < paragraphs.items.length; i++) {
      const text = paragraphs.items[i].text.trim();
      if (text) {
        lines.push(`[${i}] ${text}`);
      }
    }
    return lines.length > 0 ? lines.join("\n") : "Il documento è vuoto.";
  });
}

export async function getWordCount(): Promise<string> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("text");
    await context.sync();

    let wordCount = 0;
    let paraCount = 0;
    for (const para of paragraphs.items) {
      const text = para.text.trim();
      if (text) {
        paraCount++;
        wordCount += text.split(/\s+/).length;
      }
    }
    return `Il documento ha ${wordCount} parole in ${paraCount} paragrafi non vuoti.`;
  });
}

// ---------------------------------------------------------------------------
// Insert tools
// ---------------------------------------------------------------------------

export async function addParagraph(text: string, style?: string): Promise<string> {
  return Word.run(async (context) => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    const para = context.document.body.insertParagraph(text, Word.InsertLocation.end);
    if (style) {
      para.style = style;
    }
    await context.sync();
    return `Aggiunto paragrafo: "${text}"`;
  });
}

export async function addHeading(text: string, level: number = 1): Promise<string> {
  const clampedLevel = Math.max(1, Math.min(level, 4));
  return Word.run(async (context) => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    const para = context.document.body.insertParagraph(text, Word.InsertLocation.end);
    para.style = `Heading ${clampedLevel}`;
    await context.sync();
    return `Aggiunto titolo livello ${clampedLevel}: "${text}"`;
  });
}

export async function addPageBreak(): Promise<string> {
  return Word.run(async (context) => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    context.document.body.insertBreak(Word.BreakType.page, Word.InsertLocation.end);
    await context.sync();
    return "Aggiunta interruzione di pagina.";
  });
}

export async function addTable(rows: number, cols: number, data?: string[][]): Promise<string> {
  return Word.run(async (context) => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    // Build values array — Office.js insertTable requires a 2D string array
    const values: string[][] = [];
    for (let r = 0; r < rows; r++) {
      const row: string[] = [];
      for (let c = 0; c < cols; c++) {
        row.push(data && data[r] && data[r][c] ? String(data[r][c]) : "");
      }
      values.push(row);
    }
    context.document.body.insertTable(rows, cols, Word.InsertLocation.end, values);
    await context.sync();
    return `Aggiunta tabella ${rows}×${cols}.`;
  });
}

// ---------------------------------------------------------------------------
// Delete tools
// ---------------------------------------------------------------------------

export async function deleteParagraph(paragraphIndex: number): Promise<string> {
  return Word.run(async (context) => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("text");
    await context.sync();

    if (paragraphIndex < 0 || paragraphIndex >= paragraphs.items.length) {
      return `Errore: indice ${paragraphIndex} fuori range (il documento ha ${paragraphs.items.length} paragrafi).`;
    }
    const deletedText = paragraphs.items[paragraphIndex].text;
    paragraphs.items[paragraphIndex].delete();
    await context.sync();
    return `Eliminato paragrafo [${paragraphIndex}]: "${deletedText.substring(0, 80)}"`;
  });
}

export async function deleteSentence(sentence: string): Promise<string> {
  const target = normalizeText(sentence);
  if (!target) return "Errore: testo da cercare vuoto.";

  return Word.run(async (context) => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("text");
    await context.sync();

    for (const para of paragraphs.items) {
      if (normalizeText(para.text).includes(target)) {
        const deleted = para.text;
        para.delete();
        await context.sync();
        return `Eliminato paragrafo contenente "${sentence}": "${deleted.substring(0, 80)}"`;
      }
    }
    return `Non trovato nessun paragrafo contenente: "${sentence}"`;
  });
}

// ---------------------------------------------------------------------------
// Replace / insert-after tools
// ---------------------------------------------------------------------------

export async function replaceText(findText: string, replaceWith: string): Promise<string> {
  if (!findText) return "Errore: testo da cercare vuoto.";

  return Word.run(async (context) => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("text");
    await context.sync();

    let totalCount = 0;

    for (const para of paragraphs.items) {
      // Quick check: does this paragraph contain the text?
      if (!normalizeText(para.text).includes(normalizeText(findText))) {
        continue;
      }
      // Use paragraph-level search which is more reliable than body.search
      const results = para.search(findText, { matchCase: false });
      results.load("items");
      await context.sync();

      for (const item of results.items) {
        item.insertText(replaceWith, Word.InsertLocation.replace);
        totalCount++;
      }
      if (results.items.length > 0) {
        await context.sync();
      }
    }
    return totalCount > 0
      ? `Sostituite ${totalCount} occorrenza/e di "${findText}" con "${replaceWith}".`
      : `Testo "${findText}" non trovato nel documento.`;
  });
}

export async function insertParagraphAfter(targetText: string, newText: string): Promise<string> {
  const target = normalizeText(targetText);
  if (!target) return "Errore: testo di riferimento vuoto.";

  return Word.run(async (context) => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("text");
    await context.sync();

    for (const para of paragraphs.items) {
      if (normalizeText(para.text).includes(target)) {
        para.insertParagraph(newText, Word.InsertLocation.after);
        await context.sync();
        return `Inserito paragrafo dopo "${para.text.substring(0, 60)}": "${newText}"`;
      }
    }
    return `Non trovato nessun paragrafo contenente: "${targetText}"`;
  });
}

// ---------------------------------------------------------------------------
// Format tool
// ---------------------------------------------------------------------------

interface FormatOptions {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  font_size?: number;
  color?: string;
}

const COLOR_MAP: Record<string, string> = {
  red: "#FF0000",
  blue: "#0000FF",
  green: "#008000",
  black: "#000000",
  white: "#FFFFFF",
  yellow: "#FFFF00",
  orange: "#FFA500",
  purple: "#800080",
  gray: "#808080",
  grey: "#808080",
};

export async function formatParagraph(paragraphIndex: number, options: FormatOptions): Promise<string> {
  return Word.run(async (context) => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("text");
    await context.sync();

    if (paragraphIndex < 0 || paragraphIndex >= paragraphs.items.length) {
      return `Errore: indice ${paragraphIndex} fuori range (il documento ha ${paragraphs.items.length} paragrafi).`;
    }

    const para = paragraphs.items[paragraphIndex];
    const font = para.font;

    if (options.bold !== undefined) font.bold = options.bold;
    if (options.italic !== undefined) font.italic = options.italic;
    if (options.underline !== undefined) {
      font.underline = options.underline ? Word.UnderlineType.single : Word.UnderlineType.none;
    }
    if (options.font_size !== undefined) font.size = options.font_size;
    if (options.color !== undefined) {
      const resolved = COLOR_MAP[options.color.toLowerCase()] || options.color;
      font.color = resolved;
    }

    await context.sync();
    return `Formattato paragrafo [${paragraphIndex}]: "${para.text.substring(0, 60)}"`;
  });
}
